import type { Me, Role } from "@gmx/shared";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { ApiError, authApi } from "./api";
import { setSsoUser, ssoLogout } from "./sso";
import { FullPageError, FullPageLoader } from "../components/FullPage";
import { Forbidden } from "../components/Forbidden";
import { ChangeTemporaryPassword } from "../pages/auth/ChangeTemporaryPassword";

export const ME_KEY = ["me"] as const;

export type AuthStatus = "loading" | "authenticated" | "anonymous" | "error";

export interface AuthContextValue {
  user: Me | null;
  status: AuthStatus;
  isAdmin: boolean;
  /** role === "reseller" */
  isReseller: boolean;
  /** Resellers manage their customers at /reseller (admins use /admin). */
  canManageCustomers: boolean;
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  /** Re-fetch /api/auth/me. */
  refresh: () => Promise<Me | null>;
  /** Replace the cached user (after SSO redeem, profile edits…). */
  setUser: (user: Me | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * A new session was just established (login, SSO redeem): drop everything
 * cached for the previous identity, cancel any in-flight /me (it was sent with
 * the old cookie and would overwrite us), then store the new user.
 */
export async function adoptSession(qc: QueryClient, user: Me) {
  await qc.cancelQueries({ queryKey: ME_KEY });
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
  qc.setQueryData(ME_KEY, user);
}

async function fetchMe(): Promise<Me | null> {
  try {
    return (await authApi.me()).user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 5 * 60_000, retry: 1 });

  const user = me.data ?? null;
  const status: AuthStatus = me.isPending ? "loading" : me.isError && me.data === undefined ? "error" : user ? "authenticated" : "anonymous";

  // Keep the extension bridge in step with who's signed in.
  useEffect(() => {
    if (status === "loading") return;
    setSsoUser(user);
  }, [user, status]);

  const setUser = useCallback(
    (u: Me | null) => {
      // An in-flight /me from before the session changed must not overwrite this.
      void qc.cancelQueries({ queryKey: ME_KEY });
      qc.setQueryData(ME_KEY, u);
    },
    [qc],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await authApi.login(email.trim(), password);
      await adoptSession(qc, res.user);
      return res.user;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* already gone server-side — still clear locally */
    }
    ssoLogout();
    qc.clear();
    qc.setQueryData(ME_KEY, null);
    navigate("/login", { replace: true });
  }, [qc, navigate]);

  const refetchMe = me.refetch;
  const refresh = useCallback(async () => {
    const r = await refetchMe();
    return r.data ?? null;
  }, [refetchMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAdmin: user?.role === "admin",
      isReseller: user?.role === "reseller",
      canManageCustomers: user?.role === "reseller" || user?.role === "admin",
      isImpersonating: !!user?.impersonatorId,
      login,
      logout,
      refresh,
      setUser,
    }),
    [user, status, login, logout, refresh, setUser],
  );

  if (status === "error") {
    return <FullPageError error={me.error} onRetry={() => void me.refetch()} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>.");
  return ctx;
}

/** Like useAuth(), but null outside <AuthProvider> (components that also render signed out or in tests). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

/** The signed-in user. Only call below <RequireAuth>. */
export function useUser(): Me {
  const { user } = useAuth();
  if (!user) throw new Error("useUser() called without a signed-in user (wrap the route in <RequireAuth>).");
  return user;
}

/** Only `/next` style same-site paths are honoured. */
export function safeNext(next: string | null | undefined, fallback = "/"): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return fallback;
  return next;
}

/** Route guard: signed-in users only. Renders children or the nested <Outlet/>. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageLoader />;
  if (status !== "authenticated") {
    const next = location.pathname + location.search;
    return <Navigate to={next && next !== "/" ? `/login?next=${encodeURIComponent(next)}` : "/login"} replace />;
  }
  if (user?.mustChangePassword && !user.impersonatorId) return <ChangeTemporaryPassword />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Route guard: only these roles. Admins pass too, unless `adminRedirect` sends
 * them somewhere better (e.g. /reseller → /admin).
 */
export function RequireRole({ roles, adminRedirect, children }: { roles: Role[]; adminRedirect?: string; children?: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin" && !roles.includes("admin") && adminRedirect) return <Navigate to={adminRedirect} replace />;
  if (user.role !== "admin" && !roles.includes(user.role)) return <Forbidden />;
  return <>{children ?? <Outlet />}</>;
}

/** Route guard for /login etc.: signed-in users skip straight to the app. */
export function PublicOnly({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageLoader />;
  if (status === "authenticated") {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={safeNext(next)} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}
